import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  AlertTriangle,
  Building2,
  CreditCard,
  Filter,
  FolderPlus,
  History,
  Key,
  LayoutGrid,
  List,
  RefreshCw,
  Search,
  Sparkles,
  Ticket,
  Users,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { cn } from '../../lib/utils';
import WorkflowFoldersSidebar from './WorkflowFoldersSidebar';
import WorkflowFoldersToggleButton from './WorkflowFoldersToggleButton';
import WorkflowActionsMenu from './WorkflowActionsMenu';
import WorkflowAdvancedFiltersDrawer from './WorkflowAdvancedFiltersDrawer';
import WorkflowCreateDropdown from './WorkflowCreateDropdown';
import WorkflowMoveToFolderModal from './WorkflowMoveToFolderModal';
import WorkflowCreateFolderModal, { WorkflowFolderPermissionsModal } from './WorkflowCreateFolderModal';
import WorkflowImportDialog from './WorkflowImportDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import DeleteWorkflowModal from './DeleteWorkflowModal';
import {
  downloadPlatformWorkflow,
  safeImportErrorMessage,
  triggerBrowserDownload,
} from '../../services/workflowPortableService';
import {
  ALL_WORKFLOWS_FOLDER_ID,
  ARCHIVED_FOLDER_ID,
  UNFILED_FOLDER_ID,
  countWorkflowsInFolder,
  filterWorkflowsByFolder,
  formatApiErrorMessage,
  formatWorkflowUpdatedAt,
  getInitialFolderSidebarCollapsed,
  getWorkflowCategoryKey,
  getWorkflowDescription,
  getWorkflowFolderName,
  getWorkflowName,
  getWorkflowSenderLabel,
  getWorkflowStatusLabel,
  getWorkflowStatusValue,
  getWorkflowTriggerLabel,
  normalizeFolderList,
  normalizeWorkflowList,
  persistFolderSidebarCollapsed,
} from '../../utils/workflowLibraryUtils';
import {
  applyWorkflowFilters,
  countActiveFilters,
  createDefaultFilter,
} from '../../utils/workflowFilterUtils';
import {
  activatePlatformWorkflow,
  archivePlatformWorkflow,
  createPlatformWorkflowFolder,
  deletePlatformWorkflow,
  deletePlatformWorkflowFolder,
  duplicatePlatformWorkflow,
  fetchPlatformWorkflowFolders,
  getPlatformBuilderPath,
  getPlatformBuilderTestPath,
  movePlatformWorkflow,
  pausePlatformWorkflow,
  updatePlatformWorkflowFolder,
} from '../../services/workflowListService';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const CategoryIcon = ({ category }) => {
  const icons = {
    authentication: Key,
    agency_lifecycle: Building2,
    client_self_repair: Users,
    support_tickets: Ticket,
    billing: CreditCard,
    system_alerts: AlertTriangle,
  };
  const Icon = icons[category] || Zap;
  return <Icon className="h-4 w-4" />;
};

const WorkflowStatusBadge = ({ workflow }) => {
  const statusValue = getWorkflowStatusValue(workflow);
  const config = {
    active: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Active' },
    paused: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Paused' },
    draft: { bg: 'bg-zinc-600/50', text: 'text-zinc-400', label: 'Draft' },
    archived: { bg: 'bg-zinc-700/50', text: 'text-zinc-500', label: 'Archived' },
    failed: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Failed' },
  };
  const c = config[statusValue] || { ...config.draft, label: getWorkflowStatusLabel(workflow) };
  return <Badge className={`${c.bg} ${c.text} border-0`}>{c.label}</Badge>;
};

const SenderScopeBadge = ({ workflow }) => {
  const sender = getWorkflowSenderLabel(workflow).toLowerCase();
  if (sender === 'platform') {
    return (
      <span className="rounded bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-400">
        Platform
      </span>
    );
  }
  return (
    <span className="rounded bg-amber-500/10 px-2 py-0.5 text-xs text-amber-400">
      {getWorkflowSenderLabel(workflow)}
    </span>
  );
};

const WorkflowLibraryPanel = ({
  token,
  onStatsChange,
  onBuildWithAI,
  onCreateTemplate,
}) => {
  const navigate = useNavigate();

  const [workflows, setWorkflows] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workflowsError, setWorkflowsError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState(ALL_WORKFLOWS_FOLDER_ID);
  const [isFolderSidebarCollapsed, setIsFolderSidebarCollapsed] = useState(getInitialFolderSidebarCollapsed);
  const [viewMode, setViewMode] = useState('list');
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);

  const [deleteWorkflowTarget, setDeleteWorkflowTarget] = useState(null);
  const [moveFolderWorkflow, setMoveFolderWorkflow] = useState(null);
  const [movingWorkflow, setMovingWorkflow] = useState(false);
  const [deletingWorkflow, setDeletingWorkflow] = useState(false);

  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [folderModalMode, setFolderModalMode] = useState('create');
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderSubmitting, setFolderSubmitting] = useState(false);
  const [deleteFolderTarget, setDeleteFolderTarget] = useState(null);
  const [permissionsFolder, setPermissionsFolder] = useState(null);
  const [importDialogMode, setImportDialogMode] = useState(null); // 'file' | 'url' | null
  const [downloadingWorkflowId, setDownloadingWorkflowId] = useState(null);

  useEffect(() => {
    persistFolderSidebarCollapsed(isFolderSidebarCollapsed);
  }, [isFolderSidebarCollapsed]);

  const fetchWorkflows = useCallback(async () => {
    setLoading(true);
    setWorkflowsError(null);
    try {
      const res = await axios.get(`${API}/platform/system-automations/workflows?limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWorkflows(normalizeWorkflowList(res.data));
    } catch (err) {
      console.error('Failed to fetch workflows:', err);
      setWorkflows([]);
      setWorkflowsError(formatApiErrorMessage(err.response?.data?.detail, 'Failed to load workflows'));
      toast.error('Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchFolders = useCallback(async () => {
    try {
      const data = await fetchPlatformWorkflowFolders(token);
      setFolders(normalizeFolderList({ folders: data }));
    } catch (err) {
      console.error('Failed to fetch folders:', err);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchWorkflows();
      fetchFolders();
    }
  }, [token, fetchWorkflows, fetchFolders]);

  const foldersById = useMemo(
    () => Object.fromEntries(folders.map((f) => [f.id, f])),
    [folders],
  );

  const folderCounts = useMemo(() => {
    const counts = {
      [ALL_WORKFLOWS_FOLDER_ID]: countWorkflowsInFolder(workflows, ALL_WORKFLOWS_FOLDER_ID),
      [UNFILED_FOLDER_ID]: countWorkflowsInFolder(workflows, UNFILED_FOLDER_ID),
      [ARCHIVED_FOLDER_ID]: countWorkflowsInFolder(workflows, ARCHIVED_FOLDER_ID),
    };
    folders.forEach((folder) => {
      counts[folder.id] = countWorkflowsInFolder(workflows, folder.id);
    });
    return counts;
  }, [workflows, folders]);

  const filteredWorkflows = useMemo(() => {
    const byFolder = filterWorkflowsByFolder(workflows, selectedFolderId);
    return applyWorkflowFilters(byFolder, appliedFilters, searchQuery);
  }, [workflows, selectedFolderId, appliedFilters, searchQuery]);

  const activeFilterCount = countActiveFilters(appliedFilters);

  const handleRowClick = (e, workflowId) => {
    const target = e.target;
    const isInteractive = target.closest('button')
      || target.closest('[role="menuitem"]')
      || target.closest('[data-no-row-click]');
    if (isInteractive) return;
    navigate(getPlatformBuilderPath(workflowId));
  };

  const handleCreateWorkflow = async (option) => {
    if (option === 'scratch' || option === 'company') {
      try {
        const res = await axios.post(
          `${API}/platform/system-automations/workflows`,
          {
            name: 'Untitled Workflow',
            description: '',
            category: 'authentication',
            trigger_event: null,
            sender_scope: 'platform',
            status: 'draft',
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const workflowId = res.data.workflow?.id;
        if (workflowId) navigate(getPlatformBuilderPath(workflowId));
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to create workflow');
      }
      return;
    }
    if (option === 'ai') {
      onBuildWithAI?.();
      return;
    }
    if (option === 'template') {
      onCreateTemplate?.();
      return;
    }
    if (option === 'import-file') {
      setImportDialogMode('file');
      return;
    }
    if (option === 'import-url') {
      setImportDialogMode('url');
      return;
    }
    if (option === 'import-campaign') {
      toast.info('Import from campaign is coming soon');
      return;
    }
    if (option === 'duplicate') {
      toast.info('Select a workflow from the list and use Duplicate from the actions menu');
    }
  };

  const handleDownloadWorkflow = async (workflow) => {
    const id = workflow?.id || workflow?.workflow_id;
    if (!id || downloadingWorkflowId) return;
    setDownloadingWorkflowId(id);
    try {
      const { blob, filename } = await downloadPlatformWorkflow(
        token,
        id,
        getWorkflowName(workflow),
      );
      triggerBrowserDownload(blob, filename);
      toast.success('Workflow downloaded');
    } catch (err) {
      toast.error(safeImportErrorMessage(err, 'Unable to download this workflow.'));
    } finally {
      setDownloadingWorkflowId(null);
    }
  };

  const handleImportedWorkflow = (workflow) => {
    setImportDialogMode(null);
    toast.success('Workflow imported as draft');
    fetchWorkflows();
    onStatsChange?.();
    if (workflow?.id) {
      navigate(getPlatformBuilderPath(workflow.id));
    }
  };

  const handleActivate = async (workflowId) => {
    try {
      await activatePlatformWorkflow(token, workflowId);
      toast.success('Workflow activated');
      fetchWorkflows();
      onStatsChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate workflow');
    }
  };

  const handlePause = async (workflowId) => {
    try {
      await pausePlatformWorkflow(token, workflowId);
      toast.success('Workflow paused');
      fetchWorkflows();
      onStatsChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to pause workflow');
    }
  };

  const handleDuplicateWorkflow = async (workflowId) => {
    try {
      await duplicatePlatformWorkflow(token, workflowId);
      toast.success('Workflow duplicated');
      fetchWorkflows();
      onStatsChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to duplicate workflow');
    }
  };

  const handleArchiveWorkflow = async (workflowId) => {
    try {
      await archivePlatformWorkflow(token, workflowId);
      toast.success('Workflow archived');
      fetchWorkflows();
      onStatsChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive workflow');
    }
  };

  const handleConfirmMoveWorkflow = async (folderId) => {
    if (!moveFolderWorkflow) return;
    setMovingWorkflow(true);
    try {
      await movePlatformWorkflow(token, moveFolderWorkflow.id, { folderId });
      toast.success('Workflow moved');
      setMoveFolderWorkflow(null);
      fetchWorkflows();
      onStatsChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to move workflow');
    } finally {
      setMovingWorkflow(false);
    }
  };

  const handleConfirmDeleteWorkflow = async () => {
    if (!deleteWorkflowTarget) return;
    setDeletingWorkflow(true);
    try {
      await deletePlatformWorkflow(token, deleteWorkflowTarget.id);
      toast.success('Workflow deleted');
      setDeleteWorkflowTarget(null);
      fetchWorkflows();
      onStatsChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete workflow');
    } finally {
      setDeletingWorkflow(false);
    }
  };

  const openCreateFolderModal = () => {
    setFolderModalMode('create');
    setEditingFolder(null);
    setShowCreateFolderModal(true);
  };

  const handleFolderSubmit = async (name) => {
    setFolderSubmitting(true);
    try {
      if (folderModalMode === 'rename' && editingFolder) {
        await updatePlatformWorkflowFolder(token, editingFolder.id, { name });
        toast.success('Folder renamed');
      } else {
        await createPlatformWorkflowFolder(token, { name });
        toast.success('Folder created');
      }
      setShowCreateFolderModal(false);
      setEditingFolder(null);
      fetchFolders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save folder');
    } finally {
      setFolderSubmitting(false);
    }
  };

  const handleConfirmDeleteFolder = async () => {
    if (!deleteFolderTarget) return;
    setFolderSubmitting(true);
    try {
      await deletePlatformWorkflowFolder(token, deleteFolderTarget.id);
      toast.success('Folder deleted. Workflows moved to Unfiled.');
      if (selectedFolderId === deleteFolderTarget.id) {
        setSelectedFolderId(ALL_WORKFLOWS_FOLDER_ID);
      }
      setDeleteFolderTarget(null);
      fetchFolders();
      fetchWorkflows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete folder');
    } finally {
      setFolderSubmitting(false);
    }
  };

  const handleFolderAction = (action, folderId) => {
    const folder = folders.find((f) => f.id === folderId);
    if (action === 'rename' && folder) {
      setFolderModalMode('rename');
      setEditingFolder(folder);
      setShowCreateFolderModal(true);
    } else if (action === 'move') {
      toast.info('Folder nesting is coming soon');
    } else if (action === 'permissions') {
      setPermissionsFolder(folder);
    } else if (action === 'delete' && folder) {
      setDeleteFolderTarget(folder);
    }
  };

  return (
    <div className="workflow-library-shell flex min-h-[640px] w-full overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950/40">
      <aside
        id="workflow-folders-sidebar"
        data-workflow-folders-sidebar
        data-collapsed={isFolderSidebarCollapsed ? 'true' : 'false'}
        data-testid="workflow-folders-sidebar"
        className={cn(
          'workflow-folders-sidebar overflow-hidden transition-[width,flex-basis] duration-[220ms] ease-out',
          isFolderSidebarCollapsed ? 'w-[68px] flex-[0_0_68px]' : 'w-[292px] flex-[0_0_292px]',
        )}
      >
        <WorkflowFoldersSidebar
          collapsed={isFolderSidebarCollapsed}
          folders={folders}
          selectedFolderId={selectedFolderId}
          folderCounts={folderCounts}
          onSelectFolder={setSelectedFolderId}
          onCreateFolder={openCreateFolderModal}
          onFolderAction={handleFolderAction}
        />
      </aside>

      <main className="workflow-library-main min-w-0 flex-1 overflow-hidden">
        <div className="flex h-full min-h-0 flex-col">
          <div className="border-b border-zinc-800 px-5 py-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex min-w-0 items-start gap-3">
                <WorkflowFoldersToggleButton
                  collapsed={isFolderSidebarCollapsed}
                  onToggle={() => setIsFolderSidebarCollapsed((prev) => !prev)}
                />
                <div>
                  <h2 className="text-xl font-bold text-white">Workflow Library</h2>
                  <p className="text-sm text-zinc-400">
                    Create, manage, and automate your business processes.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-zinc-700 text-zinc-300"
                  onClick={openCreateFolderModal}
                >
                  <FolderPlus className="mr-1.5 h-4 w-4" />
                  Create folder
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-violet-500/40 bg-violet-500/10 text-violet-300"
                  onClick={() => onBuildWithAI?.()}
                >
                  <Sparkles className="mr-1.5 h-4 w-4" />
                  Build using AI
                </Button>
                <WorkflowCreateDropdown onSelect={handleCreateWorkflow} />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-b border-zinc-800 px-5 py-3">
            <div className="relative min-w-[220px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
              <Input
                placeholder="Search workflows..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="border-zinc-800 bg-zinc-900/60 pl-10 text-white"
                data-testid="workflow-library-search"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="border-zinc-700 text-zinc-300"
              title="Refresh"
              data-testid="workflow-library-refresh"
              onClick={fetchWorkflows}
            >
              <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="border-zinc-700 text-zinc-300"
              title="Recent activity"
              onClick={() => toast.info('Recent workflow activity is coming soon')}
            >
              <History className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="border-zinc-700 text-zinc-300"
              title={viewMode === 'list' ? 'Grid view' : 'List view'}
              data-testid="workflow-library-view-toggle"
              onClick={() => setViewMode((m) => (m === 'list' ? 'grid' : 'list'))}
            >
              {viewMode === 'list' ? <LayoutGrid className="h-4 w-4" /> : <List className="h-4 w-4" />}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="relative border-zinc-700 text-zinc-300"
              data-testid="workflow-advanced-filters-btn"
              onClick={() => setShowFiltersDrawer(true)}
            >
              <Filter className="mr-1.5 h-4 w-4" />
              Advanced filters
              {activeFilterCount > 0 ? (
                <span
                  className="ml-2 rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] font-semibold text-white"
                  data-testid="workflow-filter-count-badge"
                >
                  {activeFilterCount}
                </span>
              ) : null}
            </Button>
          </div>

          <div className="min-h-0 flex-1 overflow-auto p-5">
            {loading ? (
              <div className="flex h-48 items-center justify-center">
                <RefreshCw className="h-6 w-6 animate-spin text-emerald-500" />
              </div>
            ) : workflowsError ? (
              <Card className="border-zinc-800 bg-zinc-900/50">
                <CardContent className="py-12 text-center">
                  <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-red-400" />
                  <p className="text-zinc-300">Could not load workflows</p>
                  <p className="mt-1 text-sm text-zinc-500">{workflowsError}</p>
                  <Button
                    onClick={fetchWorkflows}
                    variant="outline"
                    className="mt-4 border-zinc-700 text-zinc-300"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Retry
                  </Button>
                </CardContent>
              </Card>
            ) : filteredWorkflows.length === 0 ? (
              <Card className="border-zinc-800 bg-zinc-900/50">
                <CardContent className="py-12 text-center">
                  <Zap className="mx-auto mb-4 h-12 w-12 text-zinc-600" />
                  <p className="text-zinc-400">No workflows found</p>
                  <p className="mt-1 text-sm text-zinc-500">Try a different folder or adjust your filters</p>
                </CardContent>
              </Card>
            ) : viewMode === 'grid' ? (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="workflow-grid-view">
                {filteredWorkflows.map((workflow) => (
                  <button
                    key={workflow.id}
                    type="button"
                    className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4 text-left transition-colors hover:border-zinc-700 hover:bg-zinc-800/50"
                    onClick={(e) => handleRowClick(e, workflow.id)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium text-white">{getWorkflowName(workflow)}</p>
                      <div data-no-row-click>
                        <WorkflowActionsMenu
                          workflow={workflow}
                          variant="platform"
                          onOpenBuilder={() => navigate(getPlatformBuilderPath(workflow.id))}
                          onSendTest={() => navigate(getPlatformBuilderTestPath(workflow.id))}
                          onPause={() => handlePause(workflow.id)}
                          onActivate={() => handleActivate(workflow.id)}
                          onDuplicate={() => handleDuplicateWorkflow(workflow.id)}
                          onDownload={handleDownloadWorkflow}
                          downloadingWorkflowId={downloadingWorkflowId}
                          onMoveToFolder={() => setMoveFolderWorkflow(workflow)}
                          onArchive={() => handleArchiveWorkflow(workflow.id)}
                          onDelete={() => setDeleteWorkflowTarget(workflow)}
                        />
                      </div>
                    </div>
                    <p className="mt-1 line-clamp-2 text-xs text-zinc-500">{getWorkflowDescription(workflow)}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <WorkflowStatusBadge workflow={workflow} />
                      <SenderScopeBadge workflow={workflow} />
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-zinc-800">
                <Table>
                  <TableHeader className="bg-zinc-900/80">
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-400">Workflow</TableHead>
                      <TableHead className="text-zinc-400">Folder</TableHead>
                      <TableHead className="text-zinc-400">Trigger</TableHead>
                      <TableHead className="text-zinc-400">Sender</TableHead>
                      <TableHead className="text-zinc-400">Status</TableHead>
                      <TableHead className="text-zinc-400">Last updated</TableHead>
                      <TableHead className="text-right text-zinc-400">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredWorkflows.map((workflow) => (
                      <TableRow
                        key={workflow.id}
                        className="group cursor-pointer border-zinc-800 transition-colors hover:bg-zinc-800/50"
                        onClick={(e) => handleRowClick(e, workflow.id)}
                        data-testid={`workflow-row-${workflow.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div
                              className={cn(
                                'rounded-lg p-2',
                                getWorkflowStatusValue(workflow) === 'active' ? 'bg-emerald-500/20'
                                  : getWorkflowStatusValue(workflow) === 'draft' ? 'bg-zinc-600/30' : 'bg-amber-500/20',
                              )}
                            >
                              <CategoryIcon category={getWorkflowCategoryKey(workflow)} />
                            </div>
                            <div>
                              <p className="font-medium text-white transition-colors group-hover:text-emerald-400">
                                {getWorkflowName(workflow)}
                              </p>
                              <p className="text-xs text-zinc-500">{getWorkflowDescription(workflow)}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm capitalize text-zinc-300">
                            {getWorkflowFolderName(workflow, foldersById)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <code className="rounded bg-zinc-800 px-2 py-1 text-xs text-zinc-400">
                            {getWorkflowTriggerLabel(workflow)}
                          </code>
                        </TableCell>
                        <TableCell>
                          <SenderScopeBadge workflow={workflow} />
                        </TableCell>
                        <TableCell>
                          <WorkflowStatusBadge workflow={workflow} />
                        </TableCell>
                        <TableCell className="text-sm text-zinc-400">
                          {formatWorkflowUpdatedAt(workflow.updated_at)}
                        </TableCell>
                        <TableCell className="text-right" data-no-row-click>
                          <WorkflowActionsMenu
                            workflow={workflow}
                            variant="platform"
                            menuClassName="border-zinc-700"
                            onOpenBuilder={() => navigate(getPlatformBuilderPath(workflow.id))}
                            onSendTest={() => navigate(getPlatformBuilderTestPath(workflow.id))}
                            onPause={() => handlePause(workflow.id)}
                            onActivate={() => handleActivate(workflow.id)}
                            onDuplicate={() => handleDuplicateWorkflow(workflow.id)}
                            onDownload={handleDownloadWorkflow}
                            downloadingWorkflowId={downloadingWorkflowId}
                            onMoveToFolder={() => setMoveFolderWorkflow(workflow)}
                            onArchive={() => handleArchiveWorkflow(workflow.id)}
                            onDelete={() => setDeleteWorkflowTarget(workflow)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </main>

      <WorkflowImportDialog
        open={Boolean(importDialogMode)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setImportDialogMode(null);
        }}
        mode={importDialogMode || 'file'}
        scope="platform"
        token={token}
        onImported={handleImportedWorkflow}
      />

      <WorkflowAdvancedFiltersDrawer
        open={showFiltersDrawer}
        onClose={() => setShowFiltersDrawer(false)}
        filters={advancedFilters.length ? advancedFilters : [createDefaultFilter()]}
        onChangeFilters={setAdvancedFilters}
        onApply={setAppliedFilters}
      />

      <WorkflowCreateFolderModal
        isOpen={showCreateFolderModal}
        onClose={() => {
          setShowCreateFolderModal(false);
          setEditingFolder(null);
        }}
        onSubmit={handleFolderSubmit}
        initialName={editingFolder?.name || ''}
        title={folderModalMode === 'rename' ? 'Rename folder' : 'Create folder'}
        submitting={folderSubmitting}
      />

      <WorkflowFolderPermissionsModal
        isOpen={Boolean(permissionsFolder)}
        onClose={() => setPermissionsFolder(null)}
      />

      <DeleteWorkflowModal
        isOpen={Boolean(deleteWorkflowTarget)}
        workflowName={deleteWorkflowTarget?.name}
        onCancel={() => setDeleteWorkflowTarget(null)}
        onConfirm={handleConfirmDeleteWorkflow}
        confirming={deletingWorkflow}
      />

      {deleteFolderTarget ? (
        <Dialog open onOpenChange={(open) => !open && setDeleteFolderTarget(null)}>
          <DialogContent className="max-w-md border-zinc-700 bg-zinc-900 text-white" data-testid="delete-folder-modal">
            <DialogHeader>
              <DialogTitle>Delete folder?</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Workflows in &quot;{deleteFolderTarget.name}&quot; will be moved to Unfiled. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" variant="outline" className="border-zinc-700 text-zinc-300" onClick={() => setDeleteFolderTarget(null)}>
                Cancel
              </Button>
              <Button
                type="button"
                className="bg-red-600 hover:bg-red-700"
                disabled={folderSubmitting}
                data-testid="delete-folder-confirm"
                onClick={handleConfirmDeleteFolder}
              >
                {folderSubmitting ? 'Deleting...' : 'Delete folder'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}

      <WorkflowMoveToFolderModal
        isOpen={Boolean(moveFolderWorkflow)}
        onClose={() => setMoveFolderWorkflow(null)}
        workflow={moveFolderWorkflow}
        folders={folders}
        onMove={handleConfirmMoveWorkflow}
        onCreateFolder={() => {
          setMoveFolderWorkflow(null);
          openCreateFolderModal();
        }}
        moving={movingWorkflow}
      />
    </div>
  );
};

export default WorkflowLibraryPanel;
